// Wave 20.12 — Follow button used on Drop landing pages so recipients
// can subscribe to the sender's follower-only drops without leaving
// the page.
//
// Behavior:
//   - Not authenticated → renders a "Sign in to follow" link.
//   - Authenticated + not following → "Follow"
//   - Authenticated + following      → "Following" (click to unfollow)
//   - Authenticated + own account    → hidden (nothing to follow)

import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';
import { colors } from '../theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface Props {
  userId: number;
  compact?: boolean;
}

function getJwt(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function currentUserId(): number | null {
  const jwt = getJwt();
  if (!jwt) return null;
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    return payload?.sub ?? null;
  } catch {
    return null;
  }
}

export default function FollowButton({ userId, compact }: Props) {
  const jwt = getJwt();
  const me = currentUserId();
  const [following, setFollowing] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!jwt || !userId || me === userId) return;
    let cancelled = false;
    fetch(`${API}/follow/status/${userId}`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d) setFollowing(!!d.following);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [jwt, userId, me]);

  // Own account — no follow button
  if (me === userId) return null;

  if (!jwt) {
    return (
      <Button
        size={compact ? 'small' : 'medium'}
        href={`/login?next=${encodeURIComponent(window.location.pathname)}`}
        variant="outlined"
        startIcon={<PersonAddIcon />}
        sx={{
          borderColor: colors.brand,
          color: colors.brand,
          fontWeight: 700,
          textTransform: 'none',
        }}
      >
        Sign in to follow
      </Button>
    );
  }

  const toggle = async () => {
    setBusy(true);
    const optimistic = !following;
    setFollowing(optimistic);
    try {
      const res = await fetch(`${API}/follow/${userId}`, {
        method: optimistic ? 'POST' : 'DELETE',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) throw new Error(String(res.status));
    } catch {
      // Roll back on failure.
      setFollowing(!optimistic);
    } finally {
      setBusy(false);
    }
  };

  if (following === null) {
    return (
      <Button size={compact ? 'small' : 'medium'} disabled sx={{ minWidth: 100 }}>
        <CircularProgress size={16} />
      </Button>
    );
  }

  return (
    <Button
      size={compact ? 'small' : 'medium'}
      onClick={toggle}
      disabled={busy}
      variant={following ? 'outlined' : 'contained'}
      startIcon={following ? <CheckIcon /> : <PersonAddIcon />}
      sx={{
        fontWeight: 800,
        textTransform: 'none',
        ...(following
          ? { borderColor: colors.border, color: colors.text }
          : { backgroundColor: colors.brand, color: colors.textInverse }),
      }}
    >
      {following ? 'Following' : 'Follow'}
    </Button>
  );
}
